import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuLayers, LuShieldCheck, LuGauge, LuPlug } from 'react-icons/lu';
import HeaderOne from '../../../header/HeaderOne';
import FooterOne from '../../../footer/FooterOne';
import Head from '../../../components/Head';
import '../../landing.css';

const FEATURES = [
  { icon: <LuLayers size={16} strokeWidth={2} />, title: 'One call, thousands of messages', body: 'Pass an array of recipients and bodies. We queue, throttle, and deliver — you get a single batch ID back.' },
  { icon: <LuGauge size={16} strokeWidth={2} />, title: 'Carrier-aware pacing', body: 'We stagger sends across your SIMs so carriers stay happy. No rate-limit errors, no dropped numbers.' },
  { icon: <LuShieldCheck size={16} strokeWidth={2} />, title: 'Built-in DNC list', body: 'Opt-outs are honored automatically across your account — no manual scrubbing on every campaign.' },
  { icon: <LuPlug size={16} strokeWidth={2} />, title: 'Per-message webhooks', body: 'Get a status callback for every recipient: delivered, failed, opted-out. Stream into your CRM or analytics.' },
];

export default function BulkSmsApi() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="Bulk SMS API — Send Mass Messages via REST"
        description="Send SMS to thousands of recipients in a single API call. TextLink's Bulk SMS API handles queuing, throttling, opt-outs, and per-message webhooks. JS, Python, PHP, C# SDKs."
        canonical="/bulksmsapi"
      />
      <HeaderOne />

      <main className="lp">
        <div className="lp-shell">

          <header className="lp-hero">
            <span className="lp-eyebrow">Bulk SMS API</span>
            <h1 className="lp-h1">
              Mass SMS, one API call.<br />
              <span className="lp-h1-accent">Carrier-friendly by default.</span>
            </h1>
            <p className="lp-lead">
              Push thousands of personalized SMS in a single batch. We queue, throttle, and deliver through your SIMs — and stream back per-recipient delivery status.
            </p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Get an API key <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <a className="lp-btn lp-btn--ghost" href="https://docs.textlinksms.com/" target="_blank" rel="noopener noreferrer">
                Read the docs
              </a>
            </div>
            <div className="lp-meta">
              <span>300 free messages</span>
              <span className="lp-meta-sep" />
              <span>$0.001 per SMS</span>
              <span className="lp-meta-sep" />
              <span>No 10DLC required</span>
            </div>
          </header>

          <section className="lp-section">
            <h2 className="lp-h2">One POST, full campaign</h2>
            <p className="lp-sub">Send 10 messages or 10,000 — same shape.</p>
            <pre className="lp-code"><code>{`curl -X POST https://textlinksms.com/api/send-bulk-sms \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [
      { "phone_number": "+15551234567", "text": "Hey John, your order is ready." },
      { "phone_number": "+15557654321", "text": "Hey Sara, your order is ready." }
    ]
  }'`}</code></pre>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">What you get</h2>
            <p className="lp-sub">Bulk SMS that doesn't get your numbers blocked.</p>
            <ul className="lp-feat-list">
              {FEATURES.map((f, i) => (
                <li key={i} className="lp-feat">
                  <div className="lp-feat-icon">{f.icon}</div>
                  <div className="lp-feat-text">
                    <strong>{f.title}</strong>
                    <span>{f.body}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="lp-section">
            <h2 className="lp-h2">Pacing, not throttling errors</h2>
            <p className="lp-sub">
              Bulk APIs that just hammer the carrier get your numbers flagged. Ours staggers delivery across your SIMs based on warmup status and recent volume — every message lands, just over a few minutes instead of a few seconds.
            </p>
            <div className="lp-callout">
              <strong>Prefer no code?</strong> The <Link to="/bulksms" style={{ color: 'inherit', textDecoration: 'underline' }}>Advanced Bulk SMS</Link> dashboard gives you the same engine through a UI — upload a CSV, write the message template, hit send.
            </div>
          </section>

          <section className="lp-cta">
            <h2 className="lp-h2">Send your first batch today.</h2>
            <p className="lp-sub">300 free messages on signup. No credit card.</p>
            <div className="lp-cta-row">
              <button className="lp-btn lp-btn--primary" onClick={() => navigate('/signup')}>
                Try free <LuArrowRight size={15} strokeWidth={2} />
              </button>
              <Link className="lp-btn lp-btn--ghost" to="/pricing">See pricing</Link>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
